import getMenuItems from 'util/get-menu-items';

import type React from 'react';
import { useCallback } from 'react';

import Arrow from 'assets/images/icons/Arrow.png';
import offline_status from 'assets/images/icons/offline_status1.png';
import online_status from 'assets/images/icons/online_status.png';
import { CalenderIcon, ChatIcon, VideoBorderIcon, VideoIcon } from 'assets/svg-icons';
import { WidgetCollapedType } from 'const/widget-option-type';
import { useCall } from 'context/call-context';
import isBaseNameAvailable from 'helper/is-base-name-available';
import { isNullOrEmpty } from 'helper/is-null-or-empty';
import { t } from 'i18next';
import type { MenuOption, OtherWidget, WidgetOption } from 'types';
import { MenuType } from 'types';

import classes from './chat-bubble-strip.module.scss';

interface ICollapsedBody {
  isChatEnabled: boolean;
  isCollapsed: string;
  onlineMenu: MenuOption[];
  offlineMenu: MenuOption[];
  onlineButton: boolean;
  offlineButton: boolean;
  otherWidgets: OtherWidget[];
  otherWidgetsRef: React.MutableRefObject<Map<string, boolean>>;
  schedulingOption: { enabled: boolean };
  widgetOption: WidgetOption;
  onBubbleButtonClick: () => void;
  onPressOfflineLink: () => void;
  onAppointmentClick: () => void;
  onClickToggle: (value?: WidgetCollapedType) => void;
  setOnlineButton: (value: boolean) => void;
  setOfflineButton: (value: boolean) => void;
  toggleOtherWidget: (value: OtherWidget) => void;
}

const CollapsedStripBody = ({
  isChatEnabled,
  isCollapsed,
  onlineMenu,
  offlineMenu,
  onlineButton,
  offlineButton,
  otherWidgets,
  otherWidgetsRef,
  schedulingOption,
  widgetOption,
  onBubbleButtonClick,
  onPressOfflineLink,
  onAppointmentClick,
  onClickToggle,
  setOnlineButton,
  setOfflineButton,
  toggleOtherWidget
}: // eslint-disable-next-line sonarjs/cognitive-complexity
ICollapsedBody) => {
  const { color, texts } = widgetOption;
  const iconSize = '11px';
  const { productType } = useCall();

  const primaryBtnInlineStyle = !isChatEnabled
    ? {
        border: isCollapsed === WidgetCollapedType.SEMI_COLLAPSED ? '3px solid #888888' : 'none',
        cursor: 'not-allowed',
        padding: onlineMenu.length > 0 || offlineMenu.length > 0 ? '16px 0 0 0' : '16px 0 16px 0'
      }
    : { border: isCollapsed === WidgetCollapedType.SEMI_COLLAPSED ? '3px solid rgb(160, 191, 110)' : 'none' };

  const renderPrimaryButtons = () => (
    <button
      id="conferwith-start-video-call"
      style={primaryBtnInlineStyle}
      disabled={!isChatEnabled}
      onClick={onBubbleButtonClick}
      className={
        isCollapsed !== WidgetCollapedType.SEMI_COLLAPSED
          ? classes.cwPrimaryBtnOnCollapesed
          : classes.cwPrimaryBtnOnCollapesedTwo
      }
    >
      {isBaseNameAvailable() ? <VideoBorderIcon color="#ffffff" /> : <VideoIcon color="#ffffff" />}
      {isCollapsed !== WidgetCollapedType.SEMI_COLLAPSED && (
        <span>{texts?.startCallText ? texts?.startCallText : t('chatBubbleV2.primaryVideoCallText')}</span>
      )}
    </button>
  );

  const renderMenuButton = (data: MenuOption, isOnline: boolean) => {
    const { externalFunction, link } = getMenuItems(data, isOnline ? onlineButton : offlineButton);
    if (!isNullOrEmpty(productType) && !data.enabled) {
      return;
    }
    const buttonAction = () => {
      if (link) {
        window.location.href = link || '';
        return;
      }
      if (externalFunction) {
        externalFunction();
      }
      if (data.data.type !== MenuType.JSON) return;
      if (isOnline) {
        setOnlineButton(!onlineButton);
        return;
      }
      onPressOfflineLink();
      setOfflineButton(!offlineButton);
    };

    return (
      <button
        key={data.data.icon}
        onClick={buttonAction}
      >
        <i
          className={`${data.data.icon} ${isOnline ? '' : classes.cwButtonIcons}`}
          style={{ fontSize: iconSize }}
        />
      </button>
    );
  };

  const renderSecondaryButtons = () => {
    if (isChatEnabled) {
      return onlineMenu.map((data) => renderMenuButton(data, true));
    }
    return offlineMenu.map((data) => renderMenuButton(data, false));
  };

  const renderOtherWidget = useCallback(
    () =>
      otherWidgets &&
      otherWidgets
        .filter(
          (otherWidget) =>
            otherWidget?.label &&
            ((otherWidget.id && otherWidgetsRef.current.get(otherWidget.id) !== undefined) ||
              (otherWidget.function && otherWidgetsRef.current.get(otherWidget.function) !== undefined))
        )
        .map((otherWidget) => (
          <button
            key={otherWidget.id}
            onClick={() => toggleOtherWidget(otherWidget)}
            style={{
              border: `2px solid ${color}`,
              display: otherWidget?.label?.trim() === '' || !otherWidget?.label ? 'none' : 'flex'
            }}
            className={classes.cwSecondaryBtn}
          >
            <ChatIcon color="#000000" />
          </button>
        )),
    [otherWidgetsRef, otherWidgets]
  );

  return (
    <div
      className={`${
        isCollapsed === WidgetCollapedType.SEMI_COLLAPSED ? classes.cwOnCollapsedBodyTwo : classes.cwOnCollapsedBody
      }`}
    >
      {isCollapsed === WidgetCollapedType.COLLAPSED ? (
        <div
          className={classes.cwStatus}
          style={{ color: isChatEnabled ? 'rgb(160, 191, 110)' : '#888888' }}
        >
          <img src={isChatEnabled ? online_status : offline_status} />
          {isChatEnabled && <span>{t('chatBubbleV2.live')}</span>}
        </div>
      ) : (
        <div
          role="button"
          className={classes.cwArrow}
          onClick={() => onClickToggle(undefined)}
        >
          <img
            className={classes.top}
            src={Arrow}
          />
        </div>
      )}

      <div className={classes.cwOnColBtn}>
        <div>{renderPrimaryButtons()}</div>
        {/* Online and offline Menu Buttons */}
        {isCollapsed === WidgetCollapedType.COLLAPSED && (
          <>
            <div className={classes.cwSecondayBtnOnCollapesed}>
              {!isChatEnabled ||
                (schedulingOption.enabled && (
                  <button onClick={onAppointmentClick}>
                    <CalenderIcon style={{ display: 'flex', justifyContent: 'center' }} />
                  </button>
                ))}
              {renderSecondaryButtons()}
              {/* OtherWidget Menu Buttons */}
              {renderOtherWidget()}
            </div>
          </>
        )}
      </div>
      {isCollapsed === WidgetCollapedType.COLLAPSED && (
        <div
          role="button"
          className={classes.cwArrow}
          onClick={() => onClickToggle(WidgetCollapedType.SEMI_COLLAPSED)}
        >
          <img
            className={classes.right}
            src={Arrow}
          />
        </div>
      )}
    </div>
  );
};

export default CollapsedStripBody;
